import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Cropper from 'react-easy-crop';
import { Slider, Typography, Box } from '@mui/material';
import getCroppedImg from '../../utils/cropImage';
import Modal from '../Modal';
import Button from '../Button';

interface ImageUploadProps {
  name: string;
  label?: string;
  id?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  className?: string;
  accept?: string;
  value?: File | null;
  previewUrl?: string;
  helperText?: string;
  enableCrop?: boolean;
  onBlur?: (_: any, name: string) => void;
  onChange?: (_: any, name: string) => void;
  onRemove?: () => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  label,
  id,
  disabled,
  name,
  required,
  error,
  errorMessage,
  className,
  accept = 'image/png, image/jpeg',
  value,
  previewUrl,
  helperText,
  enableCrop = false,
  onBlur,
  onChange,
  onRemove,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cropper state
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Default placeholder SVG as data URI
  const defaultImage =
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNlNWU2ZTgiLz48dGV4dCB4PSI1MCIgeT0iNTAiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzZjNzU3ZCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';

  // Generate preview URL
  const getPreviewUrl = (): string => {
    if (value instanceof File) {
      return URL.createObjectURL(value);
    }
    if (previewUrl) {
      return previewUrl;
    }
    return defaultImage;
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      // Check file size limit (1.5 MB)
      const _fileSizeInMB = file.size / (1024 * 1024);
      // if (fileSizeInMB > 1.5) {
      //   setLocalError(t('common.imageUpload.fileSizeError'));
      //   e.target.value = ''; // Reset input
      //   return;
      // }

      setLocalError(null);

      if (enableCrop) {
        const reader = new FileReader();
        reader.addEventListener('load', () => {
          setImageSrc(reader.result as string);
          setIsCropping(true);
        });
        reader.readAsDataURL(file);
      } else {
        if (onChange) {
          const syntheticEvent = {
            target: {
              value: file,
              name: name,
              files: e.target.files,
            },
          };
          onChange(syntheticEvent, name);
        }
      }
    }
    // Reset input value so same file can be selected again if cancelled
    e.target.value = '';
  };

  const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropConfirm = async () => {
    if (imageSrc && croppedAreaPixels) {
      try {
        const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
        if (croppedBlob && onChange) {
          const file = new File([croppedBlob], 'cropped-image.jpg', {
            type: 'image/jpeg',
          });
          
          const syntheticEvent = {
            target: {
              value: file,
              name: name,
              files: [file], // Mocking FileList-like behavior partially
            },
          };
          onChange(syntheticEvent, name);
        }
        setIsCropping(false);
        setImageSrc(null);
        setZoom(1);
        setLocalError(null);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleCropCancel = () => {
    setIsCropping(false);
    setImageSrc(null);
    setZoom(1);
  };

  const handleReset = () => {
    // Always clear local input first
    if (fileInputRef.current) {
        fileInputRef.current.value = '';
    }

    // Reset local states
    setImageSrc(null);
    setIsCropping(false);
    setZoom(1);
    setLocalError(null);
    
    // Call parent handler if it exists - this handles API/State cleanup
    if (onRemove) {
      onRemove();
    } else if (onChange) {
      // Only trigger null change event if onRemove is not provided
      // If onRemove is provided, parent handles the removal logic (e.g. confirmation)
      const syntheticEvent = {
        target: {
          value: null,
          name: name,
          files: null,
        },
      };
      onChange(syntheticEvent, name);
    }
  };

  const handleUploadClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className={`image-upload-wrapper ${className || ''}`}>
      <div className="d-flex flex-column flex-sm-row align-items-center gap-4">
        {/* Image Preview */}
        <div className="image-upload-preview">
          <img
            src={getPreviewUrl()}
            alt={label || 'Preview'}
            className="rounded"
            style={{
              width: '100px',
              height: '100px',
              objectFit: 'cover',
              border: '2px solid var(--bs-border-color)',
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = defaultImage;
            }}
          />
        </div>

        {/* Upload Controls */}
        <div className="d-flex flex-column gap-3 flex-grow-1">
          <div className="d-flex flex-column flex-sm-row gap-3">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUploadClick}
              disabled={disabled}
            >
              <i className="ri-upload-2-line me-2"></i>
              {t('common.imageUpload.uploadButton')}
            </button>
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={handleReset}
              disabled={disabled || (!value && !previewUrl)}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </div>

          {/* Helper Text */}
          <small className="text-muted">
            {helperText || t('common.imageUpload.helperText')}
          </small>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            id={id}
            name={name}
            type="file"
            accept={accept}
            disabled={disabled}
            className="d-none"
            onChange={handleFileChange}
            onBlur={(e) => onBlur && onBlur(e, name)}
          />

          {/* Label */}
          {label && (
            <small className="fw-medium">
              {label} {required && <span className="text-danger">*</span>}
            </small>
          )}

          {/* Error Message */}
          {(error || localError) && (
            <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback d-block">
              <div data-field={id} data-validator="notEmpty">
                {localError || errorMessage}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Crop Modal using standard Modal component */}
      <Modal
        open={isCropping}
        onClose={handleCropCancel}
        title="Crop Image"
        maxWidth="sm"
        titleClassName="text-start"
        actionClassName="justify-content-start"
        content={
          <>
            <Box sx={{ position: 'relative', width: '100%', height: 400, backgroundColor: '#333' }}>
              <Cropper
                image={imageSrc || ''}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </Box>
            <Box sx={{ mt: 3 }}>
              <Typography gutterBottom>Zoom</Typography>
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                aria-labelledby="Zoom"
                onChange={(_e: any, zoom: number | number[]) => setZoom(Number(zoom))}
              />
            </Box>
          </>
        }
        action={
          <div className="d-flex w-100">
             <Button
                className="btn btn-primary me-3"
                text="Crop & Save"
                onClick={handleCropConfirm}
             />
             <Button
                className="btn btn-outline-secondary"
                text="Cancel"
                onClick={handleCropCancel}
             />
          </div>
        }
      />
    </div>
  );
};

export default ImageUpload;
